import { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";

import { useGetNotificationsLazyQuery, GetNotificationsDocument } from "graphql/queries/get-notifications.generated";
import { useGetNotificationCountQuery } from "graphql/queries/get-notification-count.generated";
import { useUpdateNotificationMutation } from "graphql/mutations/update-notification.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { NotificationType } from "constants/field-values";
import StateChangeNotification from "./state-change-notification";
import HandoverStateChangeNotification from "./handover-state-change-notification";
import FastLaneReadyNotification from "./fast-lane-ready-notification";
import { Link } from "react-router-dom";

type NotificationStatus = "unread" | "read" | "cleared" | "all";
type NotificationMap = { [key in NotificationStatus]: any[] };

type NotificationFiltersProps = {
	filter: NotificationStatus;
	setFilter: (filter: NotificationStatus) => void;
	unreadCount: number;
};

const NotificationFilters = ({ filter, setFilter, unreadCount }: NotificationFiltersProps) => (
	<div className="notification-filters">
		<button className={`filter-btn${filter === "unread" ? " active" : ""}`} onClick={() => setFilter("unread")}>
			Unread ({unreadCount})
		</button>
		<button className={`filter-btn${filter === "read" ? " active" : ""}`} onClick={() => setFilter("read")}>
			Read
		</button>
		<button className={`filter-btn${filter === "cleared" ? " active" : ""}`} onClick={() => setFilter("cleared")}>
			Cleared
		</button>
		<button className={`filter-btn${filter === "all" ? " active" : ""}`} onClick={() => setFilter("all")}>
			All
		</button>
	</div>
);

type DashboardTabsProps = {
	tab: "unread" | "read";
	setTab: (tab: "unread" | "read") => void;
	unreadCount: number;
	hasRead: boolean;
};

const DashboardNotificationTabs = ({ tab, setTab, unreadCount, hasRead }: DashboardTabsProps) => (
	<div className="dashboard-notification-tabs">
		<button className={`dashboard-tab-btn${tab === "unread" ? " active" : ""}`} onClick={() => setTab("unread")}>
			Unread ({unreadCount})
		</button>
		{hasRead && (
			<button className={`dashboard-tab-btn${tab === "read" ? " active" : ""}`} onClick={() => setTab("read")}>
				Read
			</button>
		)}
	</div>
);

const NotificationsList = ({ isHeader, isDashboard }: { isHeader?: boolean; isDashboard?: boolean }) => {
	const [filter, setFilter] = useState<NotificationStatus>("all");
	const [dashboardTab, setDashboardTab] = useState<"unread" | "read">("unread");
	const [notificationsMap, setNotifications] = useState<NotificationMap>({
		unread: [],
		read: [],
		cleared: [],
		all: []
	});
	const { loggedUserValue } = useLoggedUser();
	const apolloClient = useApolloClient();

	const { data: notificationCountData, refetch: refetchNotificationCount } = useGetNotificationCountQuery({
		skip: !loggedUserValue.id,
		variables: { receiver_id: loggedUserValue.id }
	});

	const unreadCount = notificationCountData?.notifications_aggregate?.aggregate?.count || 0;

	const [getNotifications, { data: notificationsData }] = useGetNotificationsLazyQuery({
		variables: {
			receiver_id: loggedUserValue.id,
			...((isDashboard || isHeader) && { archived: { _eq: false } }),
			...(isHeader && { limit: 5, viewed: { _eq: false } })
		},
		fetchPolicy: "cache-and-network",
		// onCompleted: this is moved to a useEffect below because it would return data that did not match
		// the data return in the network request in some cases (probably due to cache issues).
		// happened pretty much after each notification update after the first.
		onCompleted: data => {
			console.log("getNotifications data:", data);
		}
	});

	useEffect(() => {
		if (notificationsData) {
			const newNotifications = notificationsData?.notifications.reduce(
				(prev, next) => {
					if (next.viewed && next.archived) {
						prev.cleared.push(next);
					} else if (next.viewed && !next.archived) {
						prev.read.push(next);
					} else if (!next.viewed && !next.archived) {
						prev.unread.push(next);
					}
					return prev;
				},
				{
					unread: [],
					read: [],
					cleared: [],
					all: []
				} as NotificationMap
			);

			newNotifications.all = notificationsData?.notifications || [];
			setNotifications(newNotifications);
		}
	}, [notificationsData]);

	// Fetch the notifications on mount. The header popover mounts this list each time it opens,
	// so it also gets notifications created after the last visit to /dashboard or /notifications.
	useEffect(() => {
		if (loggedUserValue.id) {
			getNotifications();
		}
	}, [isHeader, loggedUserValue.id]);

	// When isHeader is true, show the cached unread notifications first, until the query above returns
	const getCachedNotifications = () => {
		if (!isHeader || !loggedUserValue.id) return null;

		try {
			const cachedData =
				apolloClient.readQuery({
					// /dashboard cache
					query: GetNotificationsDocument,
					variables: {
						receiver_id: loggedUserValue.id,
						archived: { _eq: false }
					}
				}) ||
				apolloClient.readQuery({
					// /notifications cache
					query: GetNotificationsDocument,
					variables: {
						receiver_id: loggedUserValue.id
					}
				});

			if (cachedData?.notifications) {
				// Filter for unread notifications and take first 5
				return cachedData.notifications
					.filter(notification => !notification.viewed && !notification.archived)
					.slice(0, 5);
			}
		} catch (error) {
			// Cache miss is expected, return null
			return null;
		}
		return null;
	};

	// Show the cached notifications immediately, so the popover is not empty while the query runs
	useEffect(() => {
		if (isHeader) {
			const cachedUnreadNotifications = getCachedNotifications();
			if (cachedUnreadNotifications) {
				const dashboardNotifications = {
					unread: cachedUnreadNotifications,
					read: [],
					cleared: [],
					all: cachedUnreadNotifications
				};
				setNotifications(dashboardNotifications);
			}
		}
	}, []);

	const [updateNotification] = useUpdateNotificationMutation({
		onCompleted: () => {
			refetchNotificationCount();
			getNotifications({
				fetchPolicy: "network-only"
			});
		},
		onError: error => {
			console.error("Error updating notification:", error);
		}
	});

	const getNotificationComponent = notification => {
		switch (notification.notification_type) {
			case NotificationType.DEVICE_STATE_CHANGE:
				return (
					<StateChangeNotification
						key={notification.id}
						notification={notification}
						updateNotification={updateNotification}
					/>
				);
			case NotificationType.HANDOVER_STATE_CHANGE:
				return (
					<HandoverStateChangeNotification
						key={notification.id}
						notification={notification}
						updateNotification={updateNotification}
					/>
				);
			case NotificationType.FAST_LANE_READY:
				return (
					<FastLaneReadyNotification
						key={notification.id}
						notification={notification}
						updateNotification={updateNotification}
						isExpanded={!isHeader && !isDashboard}
					/>
				);
			default:
				return <></>;
		}
	};

	const [unreadNotifications, readNotifications, clearedNotifications] = useMemo(() => {
		return [
			(notificationsMap["unread"] || []).map(getNotificationComponent),
			(notificationsMap["read"] || []).map(getNotificationComponent),
			(notificationsMap["cleared"] || []).map(getNotificationComponent)
		];
	}, [notificationsMap]);

	const showAllClear = notificationsMap[filter].length === 0;

	const hasRead = readNotifications.length > 0;

	useEffect(() => {
		if (!hasRead && dashboardTab === "read") setDashboardTab("unread");
		else if (hasRead && unreadCount === 0) setDashboardTab("read");
	}, [hasRead, unreadCount]);

	// The /notifications page already shows "Unread (n)", "Read" and "Cleared" in its filter tabs,
	// so only the header popover shows a title above each group.
	const showGroupTitles = isHeader;

	const dashboardNotifications = dashboardTab === "unread" ? unreadNotifications : readNotifications;
	const dashboardEmpty = dashboardNotifications.length === 0;

	return (
		<div className={`notification-list-container${showAllClear ? " empty-container" : ""}`}>
			<div className="dashboard-title">
				Notifications
				{isDashboard && (
					<Link to="/notifications">
						<img src={`${process.env.PUBLIC_URL}/assets/images/icons/external-link-black.svg`} width={24} height={24} />
					</Link>
				)}
			</div>
			{isDashboard && (unreadCount > 0 || hasRead) && (
				<DashboardNotificationTabs
					tab={dashboardTab}
					setTab={setDashboardTab}
					unreadCount={unreadCount}
					hasRead={hasRead}
				/>
			)}
			{!isDashboard && !isHeader && (
				<NotificationFilters filter={filter} setFilter={setFilter} unreadCount={unreadCount} />
			)}
			<div className="notification-list-inner-container">
				{isDashboard ? (
					dashboardEmpty ? (
						<div className="notification-list-empty">
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/checkmark-in-circle.svg`} />
							<span className="no-notifications-text">All Clear!</span>
						</div>
					) : (
						<div className="notification-list">{dashboardNotifications}</div>
					)
				) : (
					<>
						{unreadNotifications.length && (filter === "unread" || filter === "all") ? (
							<>
								{showGroupTitles && (
									<div className="notification-list-header">
										<span className="notification-list-title">Unread ({unreadCount})</span>
									</div>
								)}
								<div className="notification-list">{unreadNotifications}</div>
							</>
						) : null}
						{readNotifications.length && (filter === "read" || filter === "all") ? (
							<>
								{showGroupTitles && (
									<div className="notification-list-header">
										<span className="notification-list-title">Read</span>
									</div>
								)}
								<div className="notification-list">{readNotifications}</div>
							</>
						) : null}
						{clearedNotifications.length && (filter === "cleared" || filter === "all") ? (
							<>
								{showGroupTitles && (
									<div className="notification-list-header">
										<span className="notification-list-title">Cleared</span>
									</div>
								)}
								<div className="notification-list">{clearedNotifications}</div>
							</>
						) : null}
						{showAllClear && (
							<div className="notification-list-empty">
								<img src={`${process.env.PUBLIC_URL}/assets/images/icons/checkmark-in-circle.svg`} />
								<span className="no-notifications-text">All Clear!</span>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default NotificationsList;
